import type { LocalDate } from '../dates';
import { singletonId } from '../ids';
import type { LedgerEntryLike } from '../ledger/counting';
import type { Mutation, MutationOp } from '../mutations';
import type { BuildCtx } from '../spending/types';

export type PayeeKind = 'bill' | 'utility' | 'card' | 'loan';
export type Repeat = 'monthly' | 'weekly' | 'yearly' | 'none';
export type LoanType = 'fixed' | 'flexible';

/** The payee fields core reads. apps/web maps Drizzle rows onto this. */
export interface PayeeLike {
  id: string;
  kind: PayeeKind;
  name: string;
  categoryId: string | null;
  /** v0.5: the account payments come out of (null or absent = Budget's "Pay from"). */
  accountId?: string | null;
  fundId: string | null;
  amountMinor: number | null;
  repeat: Repeat;
  everyN: number;
  dueDays: number[];
  startDate: LocalDate;
  endDate: LocalDate | null;
  paymentCount: number | null;
  trackFrom: LocalDate;
  autoPay: boolean;
  remindDays: number;
  issuer: string | null;
  last4: string | null;
  statementDay: number | null;
  /** v0.5.1: a card due N days after each statement; null or absent = the fixed day in dueDays. */
  dueDaysAfterStatement?: number | null;
  creditLimitMinor: number | null;
  openingBalanceMinor: number | null;
  /** A supplementary card's main cardholder, e.g. 'Papa' (the user pays them their share). */
  cardholder: string | null;
  lender: string | null;
  loanType: LoanType | null;
  principalMinor: number | null;
  interestBp: number | null;
  note: string | null;
  deletedAt: string | null;
}

export type DueRowStatus = 'open' | 'skipped' | 'closed';

/** A stored payment_dues row (written only when a due is acted on). */
export interface DueRowLike {
  id: string;
  payeeId: string;
  period: LocalDate;
  amountMinor: number | null;
  minimumMinor: number | null;
  status: DueRowStatus;
  deletedAt: string | null;
}

export interface PaymentLike {
  id: string;
  payeeId: string;
  dueId: string | null;
  period: LocalDate | null;
  paidDate: LocalDate;
  amountMinor: number;
  ledgerEntryId: string | null;
  note: string | null;
  deletedAt: string | null;
}

export interface InstallmentLike {
  id: string;
  cardId: string;
  item: string;
  monthlyMinor: number;
  months: number;
  /** 'YYYY-MM' */
  startMonth: string;
  categoryId: string | null;
  note: string | null;
  deletedAt: string | null;
}

/** Everything the Payments projections read. */
export interface PaymentsSnapshot {
  userId: string;
  payees: readonly PayeeLike[];
  dues: readonly DueRowLike[];
  payments: readonly PaymentLike[];
  installments: readonly InstallmentLike[];
  /** Ledger rows paid with a card (method 'card:<id>'), for statements and balances. */
  cardEntries: readonly LedgerEntryLike[];
}

/**
 * Stored status plus what is derived: 'paid' (payments add up), 'partial', 'prepaid' (scheduled
 * before the payee was added to Tahi: counts as paid, no expense, never late), 'rolled' (a card
 * statement whose unpaid rest went into the next statement).
 */
export type DueStatus = 'open' | 'partial' | 'paid' | 'skipped' | 'closed' | 'prepaid' | 'rolled';

/** A payment due as the app shows it: the schedule with the user's actions applied. */
export interface Due {
  id: string;
  payeeId: string;
  payeeKind: PayeeKind;
  payeeName: string;
  /** The scheduled due date: the key of the due (and of its stored row). */
  period: LocalDate;
  dueDate: LocalDate;
  /** What is owed: actual amount, default, estimate, or the calculated statement. */
  amountMinor: number;
  /** A utility estimate (no actual amount yet), or a card statement still being calculated. */
  estimated: boolean;
  paidMinor: number;
  /** amountMinor - paidMinor while it is open or partial; 0 otherwise. */
  remainingMinor: number;
  status: DueStatus;
  payments: PaymentLike[];
  stored: DueRowLike | null;
  autoPay: boolean;
  /** Card statements: paying one is a transfer, never spending. */
  transfer: boolean;
  card?: {
    statementDate: LocalDate;
    minimumMinor: number;
    minimumEstimated: boolean;
    carriedInMinor: number;
    purchasesMinor: number;
    installmentsMinor: number;
    openingMinor: number;
  };
}

export type PaymentsResult<T, E extends string> = ({ ok: true } & T) | { ok: false; error: E };

const APPLIER_KEYS = ['id', 'userId', 'createdAt', 'updatedAt', 'deletedAt', 'clock'] as const;

/** Same shape as the other modules' builder helpers, with source 'payments'. */
export function paymentsMutation(
  ctx: BuildCtx,
  table: string,
  op: MutationOp,
  rowId: string,
  values?: Record<string, unknown>,
  opts: { ifAbsent?: true } = {},
): Mutation {
  for (const k of APPLIER_KEYS) {
    if (values && k in values) throw new Error(`Mutation values must not include '${k}'`);
  }
  return {
    id: ctx.newId(),
    table,
    op,
    rowId,
    ...(values ? { values } : {}),
    at: ctx.now,
    source: 'payments',
    ...(opts.ifAbsent ? { ifAbsent: true as const } : {}),
  };
}

/** Deterministic ids (conventions §3): two offline devices acting on the same due converge. */
export function dueId(userId: string, payeeId: string, period: LocalDate): string {
  return singletonId(userId, `due.${payeeId}.${period}`);
}

/** The n-th payment toward a due (1-based), so the same payment made on two devices is one row. */
export function duePaymentId(
  userId: string,
  payeeId: string,
  period: LocalDate,
  n: number,
): string {
  return singletonId(userId, `pmt.${payeeId}.${period}.${n}`);
}

/** The ledger row (expense or transfer) the n-th payment toward a due writes. */
export function paymentEntryId(
  userId: string,
  payeeId: string,
  period: LocalDate,
  n: number,
): string {
  return singletonId(userId, `pay.${payeeId}.${period}.${n}`);
}

/** One installment month's expense: 'u:inst.<id>.2026-11' (short: row ids stop at 128 characters). */
export function installmentEntryId(userId: string, installmentId: string, month: string): string {
  return singletonId(userId, `inst.${installmentId}.${month}`);
}

export const cardMethod = (cardId: string) => `card:${cardId}`;
